// TODO: Future Enhancement Categories
// - Subclass analysis for SKUs (more granular than Class)
// - Division groupings for platforms
// - District-level configuration and analysis


// Day-of-Week Sales Factors by Platform - Real Data from 365 Days Sales Analysis
// Values represent "index to average" where 1.00 = average sales day
export const DAY_OF_WEEK_FACTORS = {
  ASH: {
    Sunday: 1.63,
    Monday: 1.16,
    Tuesday: 0.62,
    Wednesday: 0.69,
    Thursday: 0.92,
    Friday: 1.08,
    Saturday: 1.06
  },
  ATL: {
    Sunday: 1.09,
    Monday: 1.01,
    Tuesday: 0.84,
    Wednesday: 0.91,
    Thursday: 1.08,
    Friday: 1.11,
    Saturday: 0.95
  },
  BNA: {
    Sunday: 1.86,
    Monday: 1.58,
    Tuesday: 0.30,
    Wednesday: 0.39,
    Thursday: 1.19,
    Friday: 1.15,
    Saturday: 0.53
  },
  CAE: {
    Sunday: 1.07,
    Monday: 0.98,
    Tuesday: 0.86,
    Wednesday: 0.85,
    Thursday: 1.16,
    Friday: 1.33,
    Saturday: 0.75
  },
  GSO: {
    Sunday: 1.14,
    Monday: 1.15,
    Tuesday: 0.81,
    Wednesday: 0.87,
    Thursday: 1.05,
    Friday: 1.19,
    Saturday: 0.77
  },
  MEM: {
    Sunday: 1.29,
    Monday: 1.01,
    Tuesday: 0.73,
    Wednesday: 0.87,
    Thursday: 1.20,
    Friday: 1.15,
    Saturday: 0.75
  },
  PNS: {
    Sunday: 1.41,
    Monday: 1.07,
    Tuesday: 0.78,
    Wednesday: 0.79,
    Thursday: 0.92,
    Friday: 1.00,
    Saturday: 1.03
  },
  RDU: {
    Sunday: 1.10,
    Monday: 1.04,
    Tuesday: 0.82,
    Wednesday: 0.91,
    Thursday: 1.14,
    Friday: 1.15,
    Saturday: 0.84
  },
  SAV: {
    Sunday: 1.34,
    Monday: 1.08,
    Tuesday: 0.73,
    Wednesday: 0.78,
    Thursday: 0.94,
    Friday: 1.05,
    Saturday: 1.08
  },
  TYS: {
    Sunday: 1.25,
    Monday: 1.18,
    Tuesday: 0.72,
    Wednesday: 0.78,
    Thursday: 1.05,
    Friday: 1.14,
    Saturday: 0.87
  }
};

// Track which platforms we've already logged missing schedules for (to prevent spam)
const loggedMissingSchedules = {};

// Fixed gap calculation function
const calculateSalesDayEquivalentGap = (platform, deliverySchedule) => {
  const dayFactors = DAY_OF_WEEK_FACTORS[platform] || 
    // Default to mixed traffic pattern if platform not found
    { Sunday: 1.00, Monday: 1.00, Tuesday: 0.95, Wednesday: 1.05, Thursday: 1.10, Friday: 0.95, Saturday: 1.00 };
  
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  // Get delivery days for this platform
  let deliveryDays = deliverySchedule[platform] || [];
  
  // BUG 1 FIX: If no delivery schedule found, use sensible default (daily delivery)
  if (!deliveryDays || deliveryDays.length === 0) {
    // Only log ONCE per platform to prevent console spam
    if (!loggedMissingSchedules[platform]) {
      console.warn(`⚠️ No delivery schedule for ${platform}, using daily delivery default`);
      loggedMissingSchedules[platform] = true;
    }
    deliveryDays = [1, 2, 3, 4, 5, 6, 7]; // Daily delivery
  }
  
  // Convert delivery days to skip days for gap calculation
  const allDays = [1, 2, 3, 4, 5, 6, 7]; // Monday=1, Sunday=7
  const skipDays = allDays.filter(day => !deliveryDays.includes(day));
  
  // Debug logging for specific platforms
  if (platform === 'ATL' || platform === 'ASH') {
    console.log(`📅 Gap calculation for ${platform}:`, {
      deliveryDays,
      skipDays,
      skipCount: skipDays.length
    });
  }
  
  console.log(`Calculating sales-day-equivalent gap for ${platform}:`, skipDays);
  
  // Create delivery boolean array (true = delivery day, false = skip day)
  // Convert day names to indices: Sun=0, Mon=1, ..., Sat=6
  // But skipDays uses: Mon=1, Tue=2, ..., Sun=7
  // So we need to map: index 0 (Sun) = day 7, index 1 (Mon) = day 1, etc.
  const deliveryArray = daysOfWeek.map((day, index) => {
    const dayNumber = index === 0 ? 7 : index; // Sun=7, Mon=1, Tue=2, etc.
    return !skipDays.includes(dayNumber);
  });
  console.log('Delivery pattern:', daysOfWeek.map((day, i) => `${day}: ${deliveryArray[i] ? '✓' : '✗'}`));
  
  // Find the longest gap between deliveries
  let maxGapEquivalent = 0;
  let maxCalendarGap = 0;
  
  // For each possible starting delivery day, calculate gap to next delivery
  for (let startDay = 0; startDay < 7; startDay++) {
    if (!deliveryArray[startDay]) continue; // Skip if not a delivery day
    
    let gapEquivalent = 0;
    let calendarDays = 0;
    let checkDay = (startDay + 1) % 7;
    
    // Sum factors until we hit next delivery day
    while (!deliveryArray[checkDay] && checkDay !== startDay) {
      const dayName = daysOfWeek[checkDay];
      gapEquivalent += dayFactors[dayName] || 1.0;
      calendarDays++;
      checkDay = (checkDay + 1) % 7;
    }
    
    if (calendarDays > maxCalendarGap || gapEquivalent > maxGapEquivalent) {
      maxCalendarGap = Math.max(maxCalendarGap, calendarDays);
      maxGapEquivalent = Math.max(maxGapEquivalent, gapEquivalent);
    }
  }
  
  // Calculate the highest single-day factor for this platform
  const highestDayFactor = Math.max(...Object.values(dayFactors));
  const lowestDayFactor = Math.min(...Object.values(dayFactors));
  
  console.log(`${platform}: Max gap = ${maxCalendarGap} days = ${maxGapEquivalent.toFixed(2)} sales-days, Peak day factor = ${highestDayFactor.toFixed(2)}`);
  console.log(`${platform}: Real traffic variation = ${lowestDayFactor.toFixed(2)} (lowest) to ${highestDayFactor.toFixed(2)} (peak) - ${((highestDayFactor/lowestDayFactor - 1) * 100).toFixed(0)}% swing`);
  
  // VERIFICATION: Show which DAY_OF_WEEK_FACTORS are being used
  console.log(`\n=== 📊 SMART REORDER POINT VERIFICATION FOR ${platform} ===`);
  console.log(`Real day-of-week factors being used:`);
  Object.entries(dayFactors).forEach(([day, factor]) => {
    const isHighest = factor === highestDayFactor;
    console.log(`  ${day}: ${factor.toFixed(2)}${isHighest ? ' ← PEAK DAY (used in Smart Min calculation)' : ''}`);
  });
  console.log(`Gap calculation: During ${maxCalendarGap}-day delivery gap, system accumulates ${maxGapEquivalent.toFixed(2)} sales-day-equivalents of demand`);
  console.log(`Peak factor: Additional ${highestDayFactor.toFixed(2)} factor ensures inventory for busiest single day`);
  console.log(`Smart Min formula: Daily Sales × (${maxGapEquivalent.toFixed(2)} gap + ${highestDayFactor.toFixed(2)} peak) = Smart Reorder Point`);
  
  return { 
    calendarDays: maxCalendarGap,
    maxGapEquivalent, 
    highestDayFactor 
  };
};

const getConsecutiveNonDeliveryDays = (platform, deliverySchedule) => {
  // Legacy function - returns calendar days for backward compatibility
  const result = calculateSalesDayEquivalentGap(platform, deliverySchedule);
  return result.calendarDays;
};

// Simplified warehouse analysis (W004) - just In Stock vs Out of Stock
export const analyzeWarehouseInventory = (inventoryData) => {
  console.log('Starting WAREHOUSE inventory analysis for', inventoryData.length, 'total records');

  const analyzedData = inventoryData.map((item, index) => {
    // Simple categorization: In Stock or Out of Stock
    const status = item.onHand > 0 ? 'In Stock' : 'Out of Stock';
    const statusColor = item.onHand > 0 ? '#4caf50' : '#f44336';

    const result = {
      ...item,
      dailySales: 0, // No sales data for warehouse
      salesRank: index + 1,
      storeSkuCount: inventoryData.length,
      tier: 'N/A',
      inventoryModel: 'N/A',
      targetStock: null, // No target for warehouse
      reorderPoint: null, // No reorder point for warehouse
      status,
      statusColor,
      variance: 0,
      variancePercentage: 0,
      onHandValue: Number((item.onHand * item.actualCost).toFixed(2)),
      targetStockValue: 0,
      varianceValue: 0,
      shortageValue: 0,
      overageValue: 0,
      // No system data for warehouse
      hasSystemData: false,
      systemMin: null,
      systemMax: null,
      systemStatus: 'N/A',
      systemShortageValue: 0,
      systemOverageValue: 0,
      targetDOI: null,
      backroomQtyNeeded: 0,
      backroomValue: 0,
      isWarehouse: true
    };

    if (index < 3) {
      console.log(`Warehouse item ${index}:`, {
        sku: result.sku,
        onHand: result.onHand,
        status: result.status,
        onHandValue: result.onHandValue
      });
    }

    return result;
  });

  // Calculate summary statistics
  const inStockItems = analyzedData.filter(item => item.status === 'In Stock');
  const outOfStockItems = analyzedData.filter(item => item.status === 'Out of Stock');
  const totalValue = analyzedData.reduce((sum, item) => sum + item.onHandValue, 0);

  const summary = {
    isWarehouse: true,
    totalSkus: analyzedData.length,
    inStock: inStockItems.length,
    outOfStock: outOfStockItems.length,
    inStockValue: inStockItems.reduce((sum, item) => sum + item.onHandValue, 0),
    outOfStockValue: 0, // Out of stock has no value
    totalInventoryValue: totalValue,
    storesAnalyzed: 1, // Single warehouse

    // Percentages
    inStockPercent: analyzedData.length > 0 ? ((inStockItems.length / analyzedData.length) * 100).toFixed(1) : '0',
    outOfStockPercent: analyzedData.length > 0 ? ((outOfStockItems.length / analyzedData.length) * 100).toFixed(1) : '0',

    // No system/smart comparisons for warehouse
    systemSkuCount: 0,
    systemOutOfStock: 0,
    systemUnderstocked: 0,
    systemCorrectlyStocked: 0,
    systemOverstocked: 0
  };

  console.log('Warehouse analysis complete:', summary);

  return {
    analyzedData,
    summary
  };
};

export const analyzeInventory = (inventoryData, deliverySchedule = {}, systemLookupMap = null) => {
  // Check if this is warehouse data
  const hasWarehouseData = inventoryData.some(item => item.isWarehouse);
  if (hasWarehouseData) {
    console.log('Detected warehouse data, using simplified analysis');
    return analyzeWarehouseInventory(inventoryData);
  }

  console.log('Starting store-level inventory analysis for', inventoryData.length, 'total records');
  console.log('Using delivery schedule:', deliverySchedule);
  
  // Initialize match tracking
  let totalMatchAttempts = 0;
  let successfulMatches = 0;
  
  // Filter to active SKUs only (SKUs with sales > 0)
  const activeInventory = inventoryData.filter(item => item.unitsSold > 0);
  console.log('Active SKUs (with sales > 0):', activeInventory.length, 'records');
  console.log('Filtered out', inventoryData.length - activeInventory.length, 'inactive SKUs');
  
  // Group data by store/site for store-level analysis
  const storeGroups = {};
  activeInventory.forEach(item => {
    const storeKey = item.site;
    if (!storeGroups[storeKey]) {
      storeGroups[storeKey] = [];
    }
    storeGroups[storeKey].push(item);
  });
  
  console.log('Stores found:', Object.keys(storeGroups).length);
  
  const analyzedData = [];
  
  // Analyze each store independently
  Object.entries(storeGroups).forEach(([storeSite, storeData]) => {
    console.log(`Analyzing store: ${storeSite} (${storeData.length} SKUs)`);
    
    // Calculate gap ONCE per store (not per SKU)
    const platformCode = storeData[0]?.analysisplatform; // All items in store have same platform
    const storeGapInfo = calculateSalesDayEquivalentGap(platformCode, deliverySchedule);
    console.log(`📊 Store ${storeSite} (${platformCode}) gap calculated ONCE:`, {
      maxGap: storeGapInfo.maxGapEquivalent?.toFixed(2),
      highestFactor: storeGapInfo.highestDayFactor?.toFixed(2),
      calendarDays: storeGapInfo.calendarDays
    });
    
    // Sort SKUs by sales within this store only
    const sortedByStoreSales = storeData.sort((a, b) => b.unitsSold - a.unitsSold);
    const storeSkuCount = sortedByStoreSales.length;
    
    const storeAnalyzed = sortedByStoreSales.map((item, storeIndex) => {
      // Calculate daily sales (assuming annual data)
      const dailySales = item.unitsSold / 365;
      
      // Determine sales rank within THIS STORE (1 = highest sales in store)
      const storeRank = storeIndex + 1;
      const storePercentile = storeRank / storeSkuCount;
      
      // Use pre-calculated gap info for all items in this store
      const gapInfo = storeGapInfo; // Reuse pre-calculated value
      const consecutiveNonDeliveryDays = gapInfo.calendarDays; // For backward compatibility
      const maxGapEquivalent = gapInfo.maxGapEquivalent;
      const highestDayFactor = gapInfo.highestDayFactor;
      
      // Adjust Smart Max multipliers based on sales-day-equivalence (not calendar days)
      let topTierMultiplier, midTierMultiplier, bottomTierMultiplier;
      
      if (maxGapEquivalent <= 1.5) {
        // Frequent delivery (≤1.5 sales-day-equivalent gap)
        topTierMultiplier = 3;    // Top 10% items
        midTierMultiplier = 2;    // Top 30% items
        bottomTierMultiplier = 2; // Bottom 70% items
      } else {
        // Less frequent delivery (>1.5 sales-day-equivalent gap)
        topTierMultiplier = 4;    // Top 10% items  
        midTierMultiplier = 3;    // Top 30% items
        bottomTierMultiplier = 2; // Bottom 70% items (stays same)
      }
      
      // Calculate target stock based on tiered approach within store
      let targetStock;
      let tier;
      let inventoryModel;
      
      if (storePercentile <= 0.1) {
        // Top 10% performers in this store
        targetStock = Math.ceil(dailySales * topTierMultiplier);
        tier = 'Top 10%';
        inventoryModel = `${topTierMultiplier}x`;
      } else if (storePercentile <= 0.3) {
        // Next 20% performers in this store
        targetStock = Math.ceil(dailySales * midTierMultiplier);
        tier = 'Top 30%';
        inventoryModel = `${midTierMultiplier}x`;
      } else {
        // Bottom 70% in this store
        targetStock = Math.ceil(dailySales * bottomTierMultiplier);
        tier = 'Bottom 70%';
        inventoryModel = `${bottomTierMultiplier}x`;
      }
      
      // Calculate Smart Min (reorder point) using gap + highest day factor
      // Smart Min = Daily Sales × (Sales-Day-Equivalent Gap + Highest Day Factor)
      // This ensures adequate inventory for both the gap period AND the peak sales day
      // Uses estimated airport traffic patterns for more accurate calculations
      const smartMin = Math.max(1, Math.round(dailySales * (maxGapEquivalent + highestDayFactor)));
      const reorderPoint = smartMin;
      
      // Determine stock status
      let status;
      let statusColor;
      
      if (item.onHand === 0) {
        status = 'Out of Stock';
        statusColor = '#f44336'; // Red
      } else if (item.onHand < reorderPoint) {
        status = 'Understocked';
        statusColor = '#ff9800'; // Orange
      } else if (item.onHand <= targetStock) {
        status = 'Correctly Stocked';
        statusColor = '#4caf50'; // Green
      } else {
        status = 'Overstocked';
        statusColor = '#2196f3'; // Blue
      }
      
      // TASK 1: VARIANCE COLUMN EXPLANATION AND CALCULATION
      // Calculate variance from target (Smart Target Stock)
      const variance = item.onHand - targetStock;
      const variancePercentage = targetStock > 0 ? ((variance / targetStock) * 100).toFixed(1) : 0;
      
      // Calculate financial metrics using actual inventory cost
      const actualCost = item.actualCost || 0;
      const oldAur = item.oldAur || 0; // Keep for comparison
      const onHandValue = item.onHand * actualCost;
      const targetStockValue = targetStock * actualCost;
      const varianceValue = variance * actualCost;
      
      // VERIFICATION: Show Smart Reorder Point calculation using real DAY_OF_WEEK_FACTORS
      if (storeIndex < 3) {
        console.log(`\n=== 🎯 SMART REORDER POINT CALCULATION VERIFICATION ===`);
        console.log(`Platform: ${platformCode} | SKU: ${item.sku} | Store: ${storeSite}`);
        console.log(`Daily Sales: ${dailySales.toFixed(2)} units/day`);
        console.log(`\nUsing REAL DAY_OF_WEEK_FACTORS for ${platformCode}:`);
        
        // Show the actual factors being used from our updated DAY_OF_WEEK_FACTORS
        const platformFactors = DAY_OF_WEEK_FACTORS[platformCode];
        if (platformFactors) {
          Object.entries(platformFactors).forEach(([day, factor]) => {
            const isHighest = factor === highestDayFactor;
            console.log(`  ${day}: ${factor.toFixed(2)}${isHighest ? ' ← PEAK DAY' : ''}`);
          });
        }
        
        console.log(`\nGap Analysis:`);
        console.log(`  Calendar days gap: ${consecutiveNonDeliveryDays} days`);
        console.log(`  Sales-day equivalent: ${maxGapEquivalent.toFixed(2)} (accounts for traffic variation)`);
        console.log(`  Peak day factor: ${highestDayFactor.toFixed(2)}`);
        
        console.log(`\nSmart Min Calculation:`);
        console.log(`  Formula: Daily Sales × (Gap Equivalent + Peak Factor)`);
        console.log(`  Calculation: ${dailySales.toFixed(2)} × (${maxGapEquivalent.toFixed(2)} + ${highestDayFactor.toFixed(2)}) = ${smartMin} units`);
        console.log(`  Result: Smart Reorder Point = ${smartMin} units`);
        console.log(`  Impact: Using real ${platformCode} traffic data instead of generic patterns`);
        console.log('===============================================');
      }
      
      // TASK 1: DETAILED VARIANCE LOGGING FOR FIRST FEW ITEMS
      if (storeIndex < 5) {
        console.log('\n=== 📊 VARIANCE COLUMN EXPLANATION ===');
        console.log(`SKU: ${item.sku} at Store: ${storeSite}`);
        console.log(`FORMULA: Variance = On Hand - Smart Target Stock`);
        console.log(`CALCULATION: ${variance} units = ${item.onHand} - ${targetStock}`);
        console.log(`VARIANCE VALUE: $${varianceValue.toFixed(2)} = ${variance} units × $${actualCost.toFixed(4)} unit cost`);
        console.log(`MEANING: ${variance > 0 ? `OVERSTOCKED by ${variance} units ($${varianceValue.toFixed(2)} excess value)` : variance < 0 ? `UNDERSTOCKED by ${Math.abs(variance)} units ($${Math.abs(varianceValue).toFixed(2)} shortage value)` : 'CORRECTLY STOCKED (exactly at target)'}`);
        console.log(`BUSINESS INSIGHT: Shows deviation from optimal Smart Target Stock level`);
        console.log(`  - Positive = Too much inventory (carrying cost, obsolescence risk)`);
        console.log(`  - Negative = Too little inventory (stockout risk, lost sales)`);
        console.log(`  - Zero = Optimal inventory level`);
        console.log('===============================================');
      }
      
      // Debug logging for first few items (MOVED AFTER financial calculations)
      if (storeIndex < 3) {
        console.log(`DEBUG: Store ${storeSite}, Item ${storeIndex}: SKU ${item.sku}`);
        console.log(`  - Platform: ${platformCode} (${consecutiveNonDeliveryDays} calendar days gap)`);
        console.log(`  - Gap equivalent: ${maxGapEquivalent.toFixed(2)} sales-days`);
        console.log(`  - Highest day factor: ${highestDayFactor.toFixed(2)}`);
        console.log(`  - Daily Sales: ${dailySales.toFixed(2)} units/day`);
        console.log(`  - COST CALCULATION: OH${item.onHand} × $${actualCost.toFixed(4)} = $${onHandValue.toFixed(2)} (was AUR $${oldAur.toFixed(2)})`);
        console.log(`  - Smart Max (Target): ${targetStock} units`);
        console.log(`  - OLD Smart Min (70% method): ${Math.ceil(targetStock * 0.7)} units`);
        console.log(`  - NEW Smart Min (gap+peak method): ${smartMin} units = ${dailySales.toFixed(2)} × (${maxGapEquivalent.toFixed(2)} + ${highestDayFactor.toFixed(2)})`);
        console.log(`  - Store percentile: ${storePercentile.toFixed(3)}, Tier: ${tier}, Model: ${inventoryModel}`);
      }
      
      // Calculate shortage/overage dollars using actual cost
      let shortageValue = 0;
      let overageValue = 0;
      
      if (status === 'Out of Stock') {
        shortageValue = reorderPoint * actualCost;
      } else if (status === 'Understocked') {
        shortageValue = (reorderPoint - item.onHand) * actualCost;
      } else if (status === 'Overstocked') {
        overageValue = (item.onHand - targetStock) * actualCost;
      }

      // System Min/Max analysis
      let systemData = { hasSystemData: false, systemMin: null, systemMax: null };
      let systemStatus = 'No System Data';
      let systemShortageValue = 0;
      let systemOverageValue = 0;
      let backroomQtyNeeded = 0;
      let backroomValue = 0;
      
      if (systemLookupMap) {
        // Extract store number from site for matching
        // Store format: "2028 - ATL-2028   CNBC" → extract "2028" 
        // Also handle: "0160 - BNA-0160" → extract "160" (strip leading zeros)
        const extractStoreNumber = (storeString) => {
          if (!storeString) return '';
          const match = String(storeString).match(/^(\d+)/);
          if (!match) return String(storeString).trim();
          
          // Strip leading zeros (0160 → 160, but 0 stays as 0)
          const cleanStore = match[1].replace(/^0+/, '') || '0';
          return cleanStore;
        };
        
        const storeNumber = extractStoreNumber(item.site);
        const lookupKey = `${storeNumber}-${item.sku}`; // New format: store-sku
        const systemInfo = systemLookupMap.get(lookupKey);
        
        // Track match statistics
        totalMatchAttempts++;
        if (systemInfo) {
          successfulMatches++;
        }
        
        // Debug logging for first 5 attempts
        if (storeIndex < 5) {
          console.log(`🔍 Match attempt ${storeIndex}:`, {
            originalStore: item.site,
            extractedStore: storeNumber,
            sku: item.sku,
            lookupKey: lookupKey,
            found: !!systemInfo,
            minMaxValue: systemInfo
          });
        }
        
        if (systemInfo) {
          systemData = {
            hasSystemData: true,
            systemMin: systemInfo.min,     // Fixed: use 'min' not 'systemMin'
            systemMax: systemInfo.max      // Fixed: use 'max' not 'systemMax'
          };
          
          // Debug first few successful matches
          if (successfulMatches <= 5) {
            console.log(`✅ SAVED system data to item:`, {
              sku: item.sku,
              store: storeNumber,
              systemMin: systemData.systemMin,
              systemMax: systemData.systemMax,
              rawData: systemInfo
            });
          }
          
          // Calculate system status using actual cost
          if (item.onHand === 0) {
            systemStatus = 'System Out of Stock';
            systemShortageValue = systemData.systemMin * actualCost;
          } else if (item.onHand < systemData.systemMin) {
            systemStatus = 'System Understocked';
            systemShortageValue = (systemData.systemMin - item.onHand) * actualCost;
          } else if (item.onHand <= systemData.systemMax) {
            systemStatus = 'System Correctly Stocked';
          } else {
            systemStatus = 'System Overstocked';
            systemOverageValue = (item.onHand - systemData.systemMax) * actualCost;
          }
          
          // Calculate backroom needs (Smart Max vs System Max) using actual cost
          if (targetStock > systemData.systemMax) {
            backroomQtyNeeded = targetStock - systemData.systemMax;
            backroomValue = backroomQtyNeeded * actualCost;
          }
        }
      }

      // Calculate Target DOI (Days of Inventory) for System Max
      let targetDOI = null;
      if (systemData.hasSystemData && systemData.systemMax && dailySales > 0) {
        targetDOI = Number((systemData.systemMax / dailySales).toFixed(1));
      }

      return {
        ...item,
        dailySales: Number(dailySales.toFixed(2)),
        salesRank: storeRank,
        storeSkuCount,
        tier,
        inventoryModel,
        targetStock,
        reorderPoint,
        status,
        statusColor,
        variance,
        variancePercentage: Number(variancePercentage),
        actualCost: Number(actualCost.toFixed(4)), // Use actual cost instead of AUR
        oldAur: Number(oldAur.toFixed(2)), // Keep AUR for comparison
        onHandValue: Number(onHandValue.toFixed(2)),
        targetStockValue: Number(targetStockValue.toFixed(2)),
        varianceValue: Number(varianceValue.toFixed(2)),
        shortageValue: Number(shortageValue.toFixed(2)),
        overageValue: Number(overageValue.toFixed(2)),
        // System metrics
        ...systemData,
        systemStatus,
        systemShortageValue: Number(systemShortageValue.toFixed(2)),
        systemOverageValue: Number(systemOverageValue.toFixed(2)),
        // Target DOI (Days of Inventory)
        targetDOI,
        // Backroom metrics
        backroomQtyNeeded,
        backroomValue: Number(backroomValue.toFixed(2))
      };
    });
    
    analyzedData.push(...storeAnalyzed);
  });
  
  // Calculate summary statistics
  const totalActiveSkus = analyzedData.length;
  
  const outOfStockItems = analyzedData.filter(item => item.status === 'Out of Stock');
  const understockedItems = analyzedData.filter(item => item.status === 'Understocked');
  const correctlyStockedItems = analyzedData.filter(item => item.status === 'Correctly Stocked');
  const overstockedItems = analyzedData.filter(item => item.status === 'Overstocked');
  
  // Calculate system metrics (only for items with system data)
  const systemItems = analyzedData.filter(item => item.hasSystemData);
  const systemOutOfStock = systemItems.filter(item => item.systemStatus === 'System Out of Stock');
  const systemUnderstocked = systemItems.filter(item => item.systemStatus === 'System Understocked');
  const systemCorrectlyStocked = systemItems.filter(item => item.systemStatus === 'System Correctly Stocked');
  const systemOverstocked = systemItems.filter(item => item.systemStatus === 'System Overstocked');
  
  // Calculate backroom metrics by store
  const backroomByStore = {};
  analyzedData.forEach(item => {
    if (item.backroomQtyNeeded > 0) {
      const storeName = item.site;
      if (!backroomByStore[storeName]) {
        backroomByStore[storeName] = {
          skuCount: 0,
          totalUnits: 0,
          totalValue: 0,
          items: []
        };
      }
      backroomByStore[storeName].skuCount++;
      backroomByStore[storeName].totalUnits += item.backroomQtyNeeded;
      backroomByStore[storeName].totalValue += item.backroomValue;
      backroomByStore[storeName].items.push({
        sku: item.sku,
        description: item.description,
        qtyNeeded: item.backroomQtyNeeded,
        value: item.backroomValue
      });
    }
  });
  
  const summary = {
    // Smart metrics (existing)
    totalSkus: totalActiveSkus,
    outOfStock: outOfStockItems.length,
    understocked: understockedItems.length,
    correctlyStocked: correctlyStockedItems.length,
    overstocked: overstockedItems.length,
    storesAnalyzed: Object.keys(storeGroups).length,
    
    // Smart financial metrics
    outOfStockValue: (() => {
      const value = outOfStockItems.reduce((sum, item) => sum + item.shortageValue, 0);
      console.log(`\n=== 💰 ROW 1 DOLLAR VALUE VERIFICATION ===`);
      console.log(`OUT OF STOCK CARD: $${Math.round(value).toLocaleString()}`);
      console.log(`Formula: Sum of shortageValue for out-of-stock items`);
      console.log(`Expected: $0 (no inventory = no value)`);
      console.log(`Sample calculations:`);
      outOfStockItems.slice(0, 3).forEach(item => {
        console.log(`  SKU ${item.sku}: OnHand=${item.onHand}, ReorderPoint=${item.reorderPoint}, UnitCost=$${item.actualCost?.toFixed(4)}, ShortageValue=$${item.shortageValue?.toFixed(2)}`);
      });
      return value;
    })(),
    understockedValue: (() => {
      const value = understockedItems.reduce((sum, item) => sum + item.shortageValue, 0);
      console.log(`\nUNDERSTOCKED CARD: $${Math.round(value).toLocaleString()}`);
      console.log(`Formula: Sum of shortageValue = (reorderPoint - onHand) × unitCost`);
      console.log(`Expected: Dollar value of shortage gaps only`);
      console.log(`Sample calculations:`);
      understockedItems.slice(0, 3).forEach(item => {
        const gap = item.reorderPoint - item.onHand;
        console.log(`  SKU ${item.sku}: OnHand=${item.onHand}, ReorderPoint=${item.reorderPoint}, Gap=${gap} units, UnitCost=$${item.actualCost?.toFixed(4)}, ShortageValue=$${item.shortageValue?.toFixed(2)}`);
      });
      return value;
    })(),
    correctlyStockedValue: (() => {
      const value = correctlyStockedItems.reduce((sum, item) => sum + item.onHandValue, 0);
      console.log(`\nCORRECTLY STOCKED CARD: $${Math.round(value).toLocaleString()}`);
      console.log(`Formula: Sum of onHandValue = onHand × unitCost`);
      console.log(`Expected: Total actual inventory value of correctly stocked items`);
      console.log(`Sample calculations:`);
      correctlyStockedItems.slice(0, 3).forEach(item => {
        console.log(`  SKU ${item.sku}: OnHand=${item.onHand}, UnitCost=$${item.actualCost?.toFixed(4)}, OnHandValue=$${item.onHandValue?.toFixed(2)}`);
      });
      return value;
    })(),
    overstockedValue: (() => {
      const value = overstockedItems.reduce((sum, item) => sum + item.overageValue, 0);
      console.log(`\nOVERSTOCKED CARD: $${Math.round(value).toLocaleString()}`);
      console.log(`Formula: Sum of overageValue = (onHand - targetStock) × unitCost`);
      console.log(`Expected: Dollar value of excess above target only`);
      console.log(`Sample calculations:`);
      overstockedItems.slice(0, 3).forEach(item => {
        const excess = item.onHand - item.targetStock;
        console.log(`  SKU ${item.sku}: OnHand=${item.onHand}, TargetStock=${item.targetStock}, Excess=${excess} units, UnitCost=$${item.actualCost?.toFixed(4)}, OverageValue=$${item.overageValue?.toFixed(2)}`);
      });
      return value;
    })(),
    totalInventoryValue: (() => {
      const value = analyzedData.reduce((sum, item) => sum + item.onHandValue, 0);
      console.log(`\nTOTAL INVENTORY VALUE (Active SKUs): $${Math.round(value).toLocaleString()}`);
      console.log(`Formula: Sum of ALL onHandValue = onHand × unitCost`);
      console.log(`Expected: Total actual inventory value of ALL items`);
      console.log(`Sample calculations:`);
      analyzedData.slice(0, 3).forEach(item => {
        console.log(`  SKU ${item.sku}: OnHand=${item.onHand}, UnitCost=$${item.actualCost?.toFixed(4)}, OnHandValue=$${item.onHandValue?.toFixed(2)}, Status=${item.status}`);
      });
      console.log(`\n🔍 COMPARISON:`);
      const componentSum = (outOfStockItems.reduce((sum, item) => sum + item.shortageValue, 0)) + 
                          (understockedItems.reduce((sum, item) => sum + item.shortageValue, 0)) +
                          (correctlyStockedItems.reduce((sum, item) => sum + item.onHandValue, 0)) +
                          (overstockedItems.reduce((sum, item) => sum + item.overageValue, 0));
      console.log(`OLD Component Sum Method: $${Math.round(componentSum).toLocaleString()}`);
      console.log(`NEW Total Inventory Value: $${Math.round(value).toLocaleString()}`);
      console.log(`Difference: $${Math.round(value - componentSum).toLocaleString()}`);
      console.log(`=== END ROW 1 VERIFICATION ===\n`);
      return value;
    })(),
    
    // System metrics (new)
    systemSkuCount: systemItems.length,
    systemOutOfStock: systemOutOfStock.length,
    systemUnderstocked: systemUnderstocked.length,
    systemCorrectlyStocked: systemCorrectlyStocked.length,
    systemOverstocked: systemOverstocked.length,
    
    // System financial metrics 
    systemOutOfStockValue: (() => {
      const value = systemOutOfStock.reduce((sum, item) => sum + item.systemShortageValue, 0);
      console.log(`\n=== 🔍 SYSTEM VS SMART TOTAL VALUE COMPARISON ===`);
      console.log(`System Out of Stock Value: $${Math.round(value).toLocaleString()} (using systemShortageValue)`);
      return value;
    })(),
    systemUnderstockedValue: (() => {
      const value = systemUnderstocked.reduce((sum, item) => sum + item.systemShortageValue, 0);
      console.log(`System Understocked Value: $${Math.round(value).toLocaleString()} (using systemShortageValue)`);
      return value;
    })(),
    systemCorrectlyStockedValue: (() => {
      const value = systemCorrectlyStocked.reduce((sum, item) => sum + item.onHandValue, 0);
      console.log(`System Correctly Stocked Value: $${Math.round(value).toLocaleString()} (using onHandValue)`);
      return value;
    })(),
    systemOverstockedValue: (() => {
      const value = systemOverstocked.reduce((sum, item) => sum + item.systemOverageValue, 0);
      console.log(`System Overstocked Value: $${Math.round(value).toLocaleString()} (using systemOverageValue)`);
      return value;
    })(),
    systemTotalInventoryValue: (() => {
      const value = systemItems.reduce((sum, item) => sum + item.onHandValue, 0);
      console.log(`\nSystem Direct Total: $${Math.round(value).toLocaleString()} (direct onHandValue sum)`);
      
      // Calculate component sum for comparison
      const systemComponentSum = systemOutOfStock.reduce((sum, item) => sum + item.systemShortageValue, 0) +
                                 systemUnderstocked.reduce((sum, item) => sum + item.systemShortageValue, 0) +
                                 systemCorrectlyStocked.reduce((sum, item) => sum + item.onHandValue, 0) +
                                 systemOverstocked.reduce((sum, item) => sum + item.systemOverageValue, 0);
      console.log(`System Component Sum: $${Math.round(systemComponentSum).toLocaleString()} (component method)`);
      console.log(`System Difference: $${Math.round(value - systemComponentSum).toLocaleString()}`);
      console.log(`\n🔍 ROOT CAUSE: System calculation mixes shortage/overage values with actual values!`);
      console.log(`=== END SYSTEM VS SMART DEBUG ===\n`);
      return value;
    })(),
    
    // Backroom metrics
    backroomByStore,
    totalBackroomValue: Object.values(backroomByStore).reduce((sum, store) => sum + store.totalValue, 0),
    totalBackroomUnits: Object.values(backroomByStore).reduce((sum, store) => sum + store.totalUnits, 0),
    storesNeedingBackroom: Object.keys(backroomByStore).length
  };
  
  // Calculate percentages
  summary.outOfStockPercent = totalActiveSkus > 0 ? ((summary.outOfStock / totalActiveSkus) * 100).toFixed(1) : '0';
  summary.understockedPercent = totalActiveSkus > 0 ? ((summary.understocked / totalActiveSkus) * 100).toFixed(1) : '0';
  summary.correctlyStockedPercent = totalActiveSkus > 0 ? ((summary.correctlyStocked / totalActiveSkus) * 100).toFixed(1) : '0';
  summary.overstockedPercent = totalActiveSkus > 0 ? ((summary.overstocked / totalActiveSkus) * 100).toFixed(1) : '0';
  
  // Log min/max matching statistics
  if (totalMatchAttempts > 0) {
    const matchRate = (successfulMatches / totalMatchAttempts * 100).toFixed(1);
    console.log(`📊 Min/Max Match Results: ${successfulMatches}/${totalMatchAttempts} (${matchRate}%)`);
    
    if (matchRate < 50) {
      console.warn('⚠️ Low match rate detected - check store number formats');
    }
  }
  
  console.log('Store-level analysis complete. Summary:', summary);
  console.log(`Analyzed ${summary.storesAnalyzed} stores with ${totalActiveSkus} active SKUs`);
  
  // PLATFORM-LEVEL SYSTEM DATA SUMMARY
  console.log('=== PLATFORM-LEVEL SYSTEM DATA SUMMARY ===');
  const platformSummary = {};
  
  analyzedData.forEach(item => {
    const platform = item.analysisplatform;
    const store = item.site.split(' ')[0];
    
    if (!platformSummary[platform]) {
      platformSummary[platform] = {
        stores: new Set(),
        totalSKUs: 0,
        systemMatches: 0,
        sampleMatched: null,
        sampleUnmatched: null
      };
    }
    
    platformSummary[platform].stores.add(store);
    platformSummary[platform].totalSKUs++;
    
    if (item.hasSystemData) {
      platformSummary[platform].systemMatches++;
      if (!platformSummary[platform].sampleMatched) {
        platformSummary[platform].sampleMatched = {
          sku: item.sku,
          store: store,
          systemMin: item.systemMin,
          systemMax: item.systemMax
        };
      }
    } else {
      if (!platformSummary[platform].sampleUnmatched) {
        platformSummary[platform].sampleUnmatched = {
          sku: item.sku,
          store: store,
          site: item.site
        };
      }
    }
  });
  
  // Log summary for each platform
  Object.entries(platformSummary).forEach(([platform, data]) => {
    const storesArray = Array.from(data.stores).sort();
    console.log(`\n📊 Platform: ${platform}`);
    console.log(`   Stores in sales data: ${storesArray.join(', ')}`);
    console.log(`   Total SKUs: ${data.totalSKUs}`);
    console.log(`   System matches: ${data.systemMatches} (${((data.systemMatches / data.totalSKUs) * 100).toFixed(1)}%)`);
    
    if (data.sampleMatched) {
      console.log(`   ✓ Sample matched SKU:`, data.sampleMatched);
    }
    if (data.sampleUnmatched) {
      console.log(`   ✗ Sample unmatched SKU:`, data.sampleUnmatched);
    }
  });
  
  // Debug inventoryModel field
  const sampleModels = analyzedData.slice(0, 10).map(item => ({ 
    sku: item.sku, 
    tier: item.tier, 
    inventoryModel: item.inventoryModel 
  }));
  console.log('Sample inventory models:', sampleModels);
  
  return {
    analyzedData,
    summary
  };
};

export const filterAnalysisResults = (analyzedData, filters) => {
  console.log('=== COMPREHENSIVE FILTER DEBUG ===');
  console.log('Input data:', analyzedData.length, 'total items');
  console.log('Active filters:', filters);
  
  const originalData = [...analyzedData];
  
  // Apply ALL filters with strict AND logic
  const filtered = originalData.filter((item, index) => {
    // Debug first few items
    const isDebugItem = index < 10;
    
    if (isDebugItem) {
      console.log(`\n--- Testing item ${index} ---`);
      console.log(`SKU: ${item.sku}, Status: "${item.status}", Store: "${item.site}", Platform: ${item.analysisplatform}`);
    }
    
    // CRITICAL: Check each filter and immediately return false if any fails
    
    // Status filter
    if (filters.status && filters.status !== 'all') {
      if (item.status !== filters.status) {
        if (isDebugItem) {
          console.log(`❌ REJECTED: Status "${item.status}" !== "${filters.status}"`);
        }
        return false; // IMMEDIATELY reject
      }
      if (isDebugItem) console.log(`✓ Status filter passed`);
    }
    
    // Platform filter  
    if (filters.platform && filters.platform !== 'all') {
      if (item.analysisplatform !== filters.platform) {
        if (isDebugItem) {
          console.log(`❌ REJECTED: Platform "${item.analysisplatform}" !== "${filters.platform}"`);
        }
        return false; // IMMEDIATELY reject
      }
      if (isDebugItem) console.log(`✓ Platform filter passed`);
    }
    
    // Class filter
    if (filters.class && filters.class !== 'all') {
      if (item.class !== filters.class) {
        if (isDebugItem) {
          console.log(`❌ REJECTED: Class "${item.class}" !== "${filters.class}"`);
        }
        return false; // IMMEDIATELY reject
      }
      if (isDebugItem) console.log(`✓ Class filter passed`);
    }
    
    // Store number filter
    if (filters.storeNumber && filters.storeNumber.trim()) {
      const storeSearch = filters.storeNumber.toLowerCase();
      if (!item.site.toLowerCase().includes(storeSearch)) {
        if (isDebugItem) {
          console.log(`❌ REJECTED: Store "${item.site}" does not include "${storeSearch}"`);
        }
        return false; // IMMEDIATELY reject
      }
      if (isDebugItem) console.log(`✓ Store filter passed`);
    }
    
    // Search filter
    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase();
      const matchesSku = item.sku.toLowerCase().includes(searchTerm);
      const matchesDesc = item.description.toLowerCase().includes(searchTerm);
      if (!matchesSku && !matchesDesc) {
        if (isDebugItem) {
          console.log(`❌ REJECTED: Search "${searchTerm}" not found in SKU or description`);
        }
        return false; // IMMEDIATELY reject
      }
      if (isDebugItem) console.log(`✓ Search filter passed`);
    }
    
    if (isDebugItem) console.log('✅ ACCEPTED: Passed ALL filters');
    return true; // Only return true if ALL filters passed
  });
  
  console.log(`\n=== FILTER RESULTS ===`);
  console.log(`Input: ${originalData.length} items`);
  console.log(`Output: ${filtered.length} items`);
  console.log(`Filtered out: ${originalData.length - filtered.length} items`);
  
  // Show sample results
  if (filtered.length > 0) {
    console.log('\nSample final results:');
    filtered.slice(0, 3).forEach((item, i) => {
      console.log(`Result ${i}: SKU ${item.sku}, Status "${item.status}", Store "${item.site}"`);
    });
  } else {
    console.log('⚠️ NO ITEMS PASSED ALL FILTERS');
  }
  
  return filtered;
};

export const exportToCSV = (analyzedData, platform) => {
  const csvContent = [
    ['SKU', 'Description', 'Class', 'On Hand', 'Units Sold', 'Daily Sales', 'Target Stock', 'Reorder Point', 'Status', 'Variance', 'Variance %'].join(','),
    ...analyzedData.map(item => [
      item.sku,
      `"${item.description}"`,
      item.class,
      item.onHand,
      item.unitsSold,
      item.dailySales,
      item.targetStock,
      item.reorderPoint,
      item.status,
      item.variance,
      item.variancePercentage
    ].join(','))
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${platform}_inventory_analysis_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};